/**
 * What a line of the phone's log *is*, so the workspace can show it accordingly.
 *
 * Two logs pass through here. The **session log** is logcat in `threadtime` form, with the tester's own
 * actions interleaved by the hub (`▸ tap 0.500,0.320`). The **job log** is what a machine printed while
 * pushing a video (`[hub] downloading clip.mp4`). Without colour both are a wall of text in which the
 * three lines that matter — a crash, an ANR, a failed push — look like everything else.
 *
 * Ordered from most specific to least, and the order is the design: a failure wins over everything
 * else the same line might look like.
 */

export type LineKind = 'own' | 'error' | 'warn' | 'ok' | 'step' | 'muted' | 'plain'

//: Ordered. First match wins.
const RULES: [LineKind, RegExp][] = [
  // The tester's own actions, marked by the hub. What a reader lines the app's reaction up against.
  ['own', /(^|\s)▸\s/],
  // Failures in the words Android and our hub actually use. `E/` is logcat's error priority; an ANR is
  // written by system_server, a native crash by crash_dump — neither under the app's own tag.
  ['error', /\bANR in\b|FATAL EXCEPTION|\bE\/|^\S+\s+\S+\s+\d+\s+\d+\s+E\s|Exception\b|\bfailed\b|\berror\b|Traceback/i],
  // Our own steps in a job log — the spine of it.
  ['step', /^\s*\[hub\]/],
  // Things that went right.
  ['ok', /\bpushed\b|\bdone\b|\bcomplete\b|\bready\b/i],
  // Chatter every Android phone prints about itself, kept but not competing.
  ['muted', /\bV\/|^\S+\s+\S+\s+\d+\s+\d+\s+V\s|\bD\/chatty\b/],
  // Warnings: logcat's `W/` priority and the words a hub uses for a soft failure.
  ['warn', /\bW\/|^\S+\s+\S+\s+\d+\s+\d+\s+W\s|\bwarn(ing)?\b|\bskipped\b|\bretry/i],
]

export function classify(line: string): LineKind {
  for (const [kind, pattern] of RULES) {
    if (pattern.test(line)) return kind
  }
  return 'plain'
}
